// ---------------------------------------------------------------------------
// gradient — animated rainbow blob field (the brand gradient motif), ported
// from legacy gradientAnimation.js. Drifting radial gradients of brand hues
// are composited additively onto a `[data-gradient]` <canvas>, producing a
// slow shifting glow behind the landing hero. Honours prefers-reduced-motion.
// ---------------------------------------------------------------------------

const CIRCLES = 30;
const MIN_RADIUS = 700;
const MAX_RADIUS = 200;
const SPEED = 0.05;
const ORBIT = 200;

// Brand hue stops (HSL hue degrees) carried over from the legacy palette.
const HUES = ['242', '263', '281', '345', '14', '47'];

class Blob {
	private x: number;
	private y: number;
	private angle: number;
	private radius: number;
	private inner: string;
	private outer: string;

	constructor(w: number, h: number, hue: string) {
		this.x = Math.random() * w;
		this.y = Math.random() * h;
		this.angle = Math.random() * Math.PI * 2;
		this.radius = Math.random() * (MAX_RADIUS - MIN_RADIUS) + MIN_RADIUS;
		this.inner = `hsla(${hue}, 75%, 50%, 0.35)`;
		this.outer = `hsla(${hue}, 100%, 75%, 0)`;
	}

	draw(ctx: CanvasRenderingContext2D): void {
		this.angle += SPEED;
		const x = this.x + Math.cos(this.angle) * ORBIT;
		const y = this.y + Math.sin(this.angle) * ORBIT;
		const gradient = ctx.createRadialGradient(x, y, 0, x, y, this.radius);
		gradient.addColorStop(0, this.inner);
		gradient.addColorStop(1, this.outer);
		ctx.fillStyle = gradient;
		ctx.beginPath();
		ctx.arc(x, y, this.radius, 0, Math.PI * 2);
		ctx.fill();
	}
}

class GradientField {
	private ctx: CanvasRenderingContext2D;
	private w = 0;
	private h = 0;
	private blobs: Blob[] = [];

	constructor(
		private canvas: HTMLCanvasElement,
		private hue: string,
	) {
		const ctx = canvas.getContext('2d');
		if (!ctx) throw new Error('2d context unavailable');
		this.ctx = ctx;
		this.resize();
		window.addEventListener('resize', () => this.resize(), { passive: true });
		this.frame();
	}

	private resize(): void {
		const dpr = window.devicePixelRatio || 1;
		this.w = this.canvas.clientWidth;
		this.h = this.canvas.clientHeight;
		this.canvas.width = this.w * dpr;
		this.canvas.height = this.h * dpr;
		this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
		this.blobs = Array.from({ length: CIRCLES }, () => new Blob(this.w, this.h, this.hue));
	}

	private frame(): void {
		this.ctx.clearRect(0, 0, this.w, this.h);
		this.ctx.globalCompositeOperation = 'lighter';
		for (const blob of this.blobs) blob.draw(this.ctx);
		window.requestAnimationFrame(() => this.frame());
	}
}

export const initGradient = (): void => {
	if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
	const canvases = document.querySelectorAll<HTMLCanvasElement>('[data-gradient]');
	canvases.forEach((canvas, i) => new GradientField(canvas, HUES[i % HUES.length]));
};
